import { Helmet } from "react-helmet-async";
import HeroBuildHeader from "./HeroBuildHeader";
import BuildNav from "./BuildNav";
import BuildFilterManager from "./BuildFilterManager";
import HeroStats from "./HeroStats";
import BuildOrder from "./BuildOrder";
import SkillPath from "./SkillPath";
import { Route, useLocation } from "react-router";
import { getDeadlockDisplayName } from "lib/general-helper";
import { useJSONFetcher } from "@outplayed/json-fetcher";
import Error404 from "components/Error404";
import { COMPONENTS } from "lib/components-helper";
import BuildSummary from "./BuildSummary";
import BuildTabsContainer, { TabsData } from "./BuildTabs";
import { useEffect, useState } from "react";
import { useQueryString } from "hooks/general-hooks";
import { MediaQuery } from "@outplayed/responsive";

export interface OptionalAndSellItem {
  Cost: number;
  ItemID: number;
  ItemName: string;
  SlotType: string;
  iconPath?: string;
}

export interface Item {
  itemId: number;
  name: string;
  cost: number;
  totalCost: number;
  type: string;
  iconPath?: string;
  isComponent?: boolean;
  stats?: Record<string, string>;
  description?: string;
  shopBonus?: string;
  componentItems?: string[];
  imbuedID?: number;
  componentItemsData?: Array<{
    name: string;
    iconPath: string;
  }>;
}

export interface Ability {
  t3Desc: string;
  t2Desc: string;
  t1Desc: string;
  stats: Record<string, string | number>;
  desc: string;
  name: string;
  abilityImage: string;
  upgrades: Record<number, Record<string, string | number>>;
}

export interface Abilities {
  [key: string]: Ability;
}

const BuildPageContainer = () => {
  const [buildData, setBuildData] = useState<any>({});
  const [allBuilds, setAllBuilds] = useState<any[]>([]);
  const [tabsData, setTabsData] = useState<TabsData[]>([]);

  // Get hero name from path
  const location = useLocation();
  const pathname = location?.pathname;
  const queryParams: any = useQueryString();
  const split = pathname.split("/");
  let heroDataName = split && split[3];

  if (heroDataName === "mo_and_krill") {
    heroDataName = "mo_&_krill";
  }
  const heroDisplayName = getDeadlockDisplayName(heroDataName);

  // Get data for hero
  const {
    data: buildData1,
    loading: buildData1IsLoading,
    error: buildData1Error,
  } = useJSONFetcher(`https://stats2.u.gg/Deadlock/final_builds/current/combined/${heroDataName}_build_0.json`, {
    ssr: true,
  });
  const {
    data: buildData2,
    loading: buildData2IsLoading,
    error: buildData2Error,
  } = useJSONFetcher(`https://stats2.u.gg/Deadlock/final_builds/current/combined/${heroDataName}_build_1.json`, {
    ssr: true,
  });
  const {
    data: buildData3,
    loading: buildData3IsLoading,
    error: buildData3Error,
  } = useJSONFetcher(`https://stats2.u.gg/Deadlock/final_builds/current/combined/${heroDataName}_build_2.json`, {
    ssr: true,
  });

  const {
    data: allHerosStaticData,
    loading: allHerosStaticDataIsLoading,
    error: allHerosStaticDataError,
  } = useJSONFetcher("https://static.bigbrain.gg/deadlock/staticdata/gamedata/Heroes_English.json", { ssr: true });

  const {
    data: allItemsStaticData,
    loading: allItemsStaticDataIsLoading,
    error: allItemsStaticDataError,
  } = useJSONFetcher("https://static.bigbrain.gg/deadlock/staticdata/gamedata/Items_English.json", { ssr: true });

  const tabsOnClick = (buildIndex: number) => {
    setBuildData(allBuilds[buildIndex]);
  };

  useEffect(() => {
    if (!buildData1IsLoading && !buildData2IsLoading && !buildData3IsLoading) {
      const builds = [buildData1, buildData2, buildData3];

      const tabs: TabsData[] = [];
      builds.forEach((build, idx) => {
        tabs.push({
          tabName: build?.buildName,
          isRecommended: idx === 0,
          buildIndex: idx,
          firstAbility: build?.abilities?.[0]?.abilityName,
        });
      });
      setTabsData(tabs);
      setAllBuilds(builds);

      const queryParamsBuildName = queryParams.multi === undefined ? "" : queryParams.multi;
      const selectedBuildIndex = builds.findIndex((build) => build?.buildName === queryParamsBuildName);
      setBuildData(builds[selectedBuildIndex === -1 ? 0 : selectedBuildIndex]);
    }
  }, [buildData1IsLoading, buildData2IsLoading, buildData3IsLoading, buildData1, buildData2, buildData3]);

  if (
    buildData1IsLoading ||
    buildData2IsLoading ||
    buildData3IsLoading ||
    allHerosStaticDataIsLoading ||
    allItemsStaticDataIsLoading
  ) {
    return <div className="deadlock-page-container" />;
  }

  // If no data for this hero - 404
  if (!buildData1IsLoading && !buildData2IsLoading && !buildData3IsLoading && (!buildData1 || buildData1Error)) {
    if (buildData1Error) {
      console.error("Error: ", buildData1Error);
    }
    return <Route component={Error404} />;
  }

  const heroSplashImg = `https://static.bigbrain.gg/assets/deadlock/hero-backgrounds/${heroDataName}-bg.jpg`;
  const metaTitle = `${heroDisplayName} Build with Highest Winrate - Deadlock Items, Ability Order, and more`;
  const metaDescription = `${heroDisplayName} build with the highest winrate item buy and sell priority. U.GG analyzes thousands of Deadlock matches to give you the best Deadlock hero build.`;

  const heroStaticData = allHerosStaticData?.[heroDataName] ?? {};
  const heroAbilities = heroStaticData?.Abilities ?? {};

  const heroImagePath = heroStaticData?.IconHeroCard ?? "";

  const tierNumber = buildData?.heroStats?.Tier ?? 0;
  const abilitiesByPickOrder = buildData?.abilities ?? [];
  const buildWinRate = buildData?.buildWinrate ?? 0;
  const buildNumMatches = buildData?.games ?? 0;
  const buildColors = buildData?.barColors ?? {};
  const heroStatsData = {
    tierNumber,
    winRate: buildData?.heroStats?.WinPercentage ?? 0,
    pickRate: buildData?.heroStats?.PickPercentage ?? 0,
    kda: buildData?.heroStats?.KDA ?? 0,
    games: buildData?.heroStats?.Games ?? 0,
    rank: buildData?.rank ?? 0,
  };

  // Sort the items by cost into early/mid/late game arrays
  // Also adds the iconPath from static data for each item and indicates if it's a component
  const itemsByStage: {
    earlyGameItems: Item[];
    midGameItems: Item[];
    lateGameItems: Item[];
  } = {
    earlyGameItems: [],
    midGameItems: [],
    lateGameItems: [],
  };
  const allItems = buildData?.items ?? [];
  const upgradedItems: Item[] = [];
  allItems.map((item: Item) => {
    if (Object.keys(COMPONENTS).includes(item.name)) {
      upgradedItems.push(item);
    }
  });
  allItems.forEach((item: Item) => {
    for (const key in allItemsStaticData) {
      if (allItemsStaticData[key].id === item.itemId) {
        item.iconPath = allItemsStaticData[key].iconPath;
        item.stats = allItemsStaticData[key].stats;
        item.description = allItemsStaticData[key].description;
        item.componentItems = allItemsStaticData[key].componentItems;
        item.shopBonus = allItemsStaticData[key].shopBonus;
        break;
      }
    }

    //Chelsea's item component related code
    upgradedItems.forEach((upgradedItem) => {
      if (COMPONENTS[upgradedItem.name] === item.name) {
        item.isComponent = true;
      }
    });

    // Processing component items data -- FROM CALIN for tooltips
    if (item.componentItems && item.componentItems.length > 0) {
      item.componentItemsData = item.componentItems.map((componentKey) => {
        const componentItemData = allItemsStaticData[componentKey];
        if (componentItemData) {
          return {
            name: componentItemData.name,
            iconPath: componentItemData.iconPath,
          };
        } else {
          // Handle case where component item is not found
          return {
            name: componentKey,
            iconPath: "",
          };
        }
      });
    }

    // Assign item to the appropriate stage
    if (item.totalCost <= 5000) {
      itemsByStage.earlyGameItems.push(item);
    } else if (item.totalCost <= 20000) {
      itemsByStage.midGameItems.push(item);
    } else {
      itemsByStage.lateGameItems.push(item);
    }
  });

  function enrichItems(items: OptionalAndSellItem[], allItemsStaticData: Record<string, any>): Item[] {
    return items.map((item) => {
      const staticItem = Object.values(allItemsStaticData).find((staticItem) => staticItem.id === item.ItemID);

      if (staticItem) {
        // Process component items data
        const componentItemsData = (staticItem.componentItems || []).map((componentKey: string) => {
          const componentItemData = allItemsStaticData[componentKey];
          return componentItemData
            ? {
                name: componentItemData.name,
                iconPath: componentItemData.iconPath,
                type: componentItemData.slotType,
              }
            : {
                name: componentKey,
                iconPath: "",
                type: "",
              };
        });

        return {
          itemId: item.ItemID,
          name: item.ItemName,
          cost: item.Cost,
          totalCost: item.Cost,
          type: item.SlotType,
          iconPath: item.iconPath || staticItem.iconPath,
          stats: staticItem.stats || {},
          description: staticItem.description || "",
          shopBonus: staticItem.shopBonus || "",
          componentItems: staticItem.componentItems || [],
          componentItemsData: componentItemsData,
        };
      } else {
        // If static data is not found
        return {
          itemId: item.ItemID,
          name: item.ItemName,
          cost: item.Cost,
          totalCost: item.Cost,
          type: item.SlotType,
          iconPath: item.iconPath,
          stats: {},
          description: "",
          shopBonus: "",
          componentItems: [],
          componentItemsData: [],
        };
      }
    });
  }

  const optionalEarlyItems = enrichItems((buildData?.notableItems ?? []).slice(0, 3), allItemsStaticData);

  const optionalLateItems = enrichItems((buildData?.notableLateItems ?? []).slice(0, 3), allItemsStaticData);

  const sellList = buildData?.centroidItemsSold ?? [];
  const sellItemsMid = enrichItems(sellList.slice(0, 3), allItemsStaticData);

  const finalBuild = enrichItems(buildData?.finalBuild ?? [], allItemsStaticData);

  const backgroundStyle = {
    backgroundRepeat: "no-repeat",
    backgroundSize: "1014px 570px",
    backgroundPosition: "50% -60px",
    backgroundImage: `
      radial-gradient(400px 200px at 60% 34%, rgba(7, 7, 32, 0) 0%, #070720 100%),
      linear-gradient(90deg, #070720 0%, rgba(7, 7, 32, 0.6) 100%),
      url(${heroSplashImg})
    `,
  };

  return (
    <div className="flex mx-auto w-full max-md:w-full">
      <div className="deadlock-page-container content-side-padding" style={backgroundStyle}>
        <Helmet>
          <title>{metaTitle}</title>
          <meta name="description" content={metaDescription} />
        </Helmet>
        <HeroBuildHeader
          heroName={heroDisplayName}
          heroImagePath={heroImagePath}
          tierNumber={tierNumber}
          abilities={heroAbilities}
        />
        <BuildNav heroName={heroDisplayName} />
        {/* <BuildFilterManager /> */}
        <HeroStats data={heroStatsData} />
        <MediaQuery min="TABLET" max="DESKTOP_LARGE" renderNullOnFail>
          <BuildTabsContainer tabsData={tabsData} onClick={tabsOnClick} abilitiesBySlot={heroAbilities} />
        </MediaQuery>
        <BuildSummary
          winRate={buildWinRate}
          matches={buildNumMatches}
          barColors={buildColors}
          abilitiesBySlot={heroAbilities}
          abilitiesByPickOrder={abilitiesByPickOrder}
          finalBuild={finalBuild}
        />
        <SkillPath abilitiesBySlot={heroAbilities} abilitiesByPickOrder={abilitiesByPickOrder} />
        <BuildOrder
          items={itemsByStage}
          optionalEarlyItems={optionalEarlyItems}
          optionalLateItems={optionalLateItems}
          sellItems={sellItemsMid}
        />
      </div>
    </div>
  );
};

export default BuildPageContainer;
